import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Grid, Button, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { getUserGotyList } from '../services/goty_api';
import { UserGotyListItem } from '../types/Goty';
import CreateGotyDialog from './dialogs/CreateGotyDialog';

interface GotyTabProps {
  username: string;
  isPublic?: boolean;
}

const GotyTab: React.FC<GotyTabProps> = ({ username, isPublic = false }) => {
  const navigate = useNavigate();
  const [gotyList, setGotyList] = useState<UserGotyListItem[]>([]);
  const [createGotyDialogOpen, setCreateGotyDialogOpen] = useState(false);

  const fetchGotyList = useCallback(async () => {
    try {
      if (username) {
        const response = await getUserGotyList(username);
        setGotyList(response.gotys);
      }
    } catch (error) {
      console.error('GOTYリストの取得に失敗しました:', error);
    }
  }, [username]);

  useEffect(() => {
    fetchGotyList();
  }, [fetchGotyList]);

  return (
    <Box>
      {!isPublic && (
        <Button
          onClick={() => setCreateGotyDialogOpen(true)}
          variant="contained"
          sx={{
            mb: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: 400,
            letterSpacing: '0.02em',
            textTransform: 'none',
            borderRadius: '6px',
            px: 2,
            py: 0.5,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              transform: 'translateY(-1px)'
            },
            '&:active': {
              transform: 'translateY(0)'
            }
          }}
        >
          新規GOTYを作成
        </Button>
      )}

      <Grid container spacing={2}>
        {gotyList.map((goty) => (
          <Grid item xs={12} sm={6} md={4} key={`${goty.year}-${goty.is_my_goty}`}>
            <Box
              onClick={() => {
                const path = goty.is_my_goty
                  ? `/mygoty/${goty.year}/${username}`
                  : `/goty/${goty.year}/${username}`;
                navigate(path);
              }}
              sx={{
                cursor: 'pointer',
                p: 3,
                borderRadius: 2,
                backgroundColor: goty.is_my_goty
                  ? 'rgba(255, 215, 0, 0.08)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: goty.is_my_goty
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(255, 255, 255, 0.1)',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: goty.is_my_goty
                    ? 'rgba(255, 215, 0, 0.15)'
                    : 'rgba(255, 255, 255, 0.08)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {goty.is_my_goty ? 'MyGOTY' : 'GOTY'} {goty.year}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {goty.selection_count} 件の選択
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                    更新: {new Date(goty.updated_at).toLocaleDateString('ja-JP')}
                  </Typography>
                </Box>
                {!isPublic && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      const editPath = goty.is_my_goty
                        ? `/mygoty/${goty.year}/edit`
                        : `/goty/${goty.year}/edit`;
                      navigate(editPath);
                    }}
                    size="small"
                    sx={{
                      opacity: 0.7,
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {gotyList.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            まだGOTYが作成されていません
          </Typography>
        </Box>
      )}

      <CreateGotyDialog
        open={createGotyDialogOpen}
        onClose={() => setCreateGotyDialogOpen(false)}
      />
    </Box>
  );
};

export default GotyTab;
